///
/// EarthBrowser
///
/// Created by Matt Giger on 2/3/2025
/// Copyright (c) 2025 Geodata Labs
///

import { requestRender } from "../engine/RenderRequest"
import { StovokorContext } from "../StovokorContext"
import { type TextureInterface } from "./Texture"
import { adjustTextureMemory, estimateTextureBytes } from "./TextureMemory"

export class TextureCube implements TextureInterface {
	public available: boolean = false
	public width: number = 0
	public height: number = 0
	public textureId: WebGLTexture | null = null
	public minFilter: number
	public magFilter: number
	public wrapS: number
	public wrapT: number

	private internalFormat: GLint
	private imageFormat: GLint
	private dataType: GLint

	// Estimated GPU bytes currently allocated across all six faces, mirrored
	// into the global TextureMemory counters on every (re)allocation and free
	private allocatedBytes: number = 0

	private context: StovokorContext

	constructor(
		context: StovokorContext,
		width: number = 1,
		height: number = 1,
		internalFormat: GLint = WebGL2RenderingContext.RGBA,
		imageFormat: GLint = WebGL2RenderingContext.RGBA,
		dataType: GLint = WebGL2RenderingContext.UNSIGNED_BYTE,
		minFilter: number = WebGL2RenderingContext.LINEAR_MIPMAP_NEAREST,
		magFilter: number = WebGL2RenderingContext.LINEAR,
		wrapS: number = WebGL2RenderingContext.CLAMP_TO_EDGE,
		wrapT: number = WebGL2RenderingContext.CLAMP_TO_EDGE
	) {
		this.context = context
		this.width = width
		this.height = height
		this.internalFormat = internalFormat
		this.imageFormat = imageFormat
		this.dataType = dataType
		this.minFilter = minFilter
		this.magFilter = magFilter
		this.wrapS = wrapS
		this.wrapT = wrapT

		if(dataType !== WebGL2RenderingContext.UNSIGNED_BYTE) {
			this.minFilter = WebGL2RenderingContext.NEAREST
			this.magFilter = WebGL2RenderingContext.NEAREST
		}
	}

	/**
	 * Update the global texture memory counters after a (re)allocation of the
	 * cube's six faces.
	 */
	private accountAllocation(mipmapped: boolean): void {
		const bytes = estimateTextureBytes(this.internalFormat, this.dataType, this.width, this.height, mipmapped, 6)
		adjustTextureMemory(bytes - this.allocatedBytes, this.allocatedBytes === 0 && bytes > 0 ? 1 : 0)
		this.allocatedBytes = bytes
		requestRender()
	}

	public initialize(width: number, height: number): void {

		this.width = width
		this.height = height

		let data: ArrayBufferView = new Uint8Array(0)
		switch(this.internalFormat) {
			case WebGL2RenderingContext.R32F:
				data = Float32Array.from([0])
				break
			case WebGL2RenderingContext.R8I:
				data = Int8Array.from([0])
				break
			case WebGL2RenderingContext.R8UI:
				data = Uint8Array.from([0])
				break
			case WebGL2RenderingContext.R16I:
				data = Int16Array.from([0])
				break
			case WebGL2RenderingContext.R16UI:
				data = Uint16Array.from([0])
				break
			case WebGL2RenderingContext.R32I:
				data = Int32Array.from([0])
				break
			case WebGL2RenderingContext.R32UI:
				data = Uint32Array.from([0])
				break
			case WebGL2RenderingContext.RGBA:
				data = Uint8Array.from([0, 0, 0, 255])
				break
			case WebGL2RenderingContext.LUMINANCE:
				data = Uint8Array.from([0])
				break
			default:
				this.context.logger.error(`Unsupported internal format: ${this.internalFormat}`)
				break
		}

		const gl = this.context.gl

		this.textureId ??= gl.createTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_CUBE_MAP, this.textureId)
		for(let i = 0; i < 6; i++) {
			gl.texImage2D(WebGL2RenderingContext.TEXTURE_CUBE_MAP_POSITIVE_X + i, 0, this.internalFormat, this.width, this.height, 0,
				this.imageFormat, this.dataType, data)
		}
		gl.texParameteri(WebGL2RenderingContext.TEXTURE_CUBE_MAP, WebGL2RenderingContext.TEXTURE_MIN_FILTER, this.minFilter)
		gl.texParameteri(WebGL2RenderingContext.TEXTURE_CUBE_MAP, WebGL2RenderingContext.TEXTURE_MAG_FILTER, this.magFilter)
		gl.texParameteri(WebGL2RenderingContext.TEXTURE_CUBE_MAP, WebGL2RenderingContext.TEXTURE_WRAP_S, this.wrapS)
		gl.texParameteri(WebGL2RenderingContext.TEXTURE_CUBE_MAP, WebGL2RenderingContext.TEXTURE_WRAP_T, this.wrapT)
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_CUBE_MAP, null)
		this.accountAllocation(false)
	}

	public destroy(): void {
		this.available = false
		if(this.textureId !== null) {
			this.context.glState.forgetTexture(this.textureId)
			this.context.gl.deleteTexture(this.textureId)
			this.textureId = null
		}
		if(this.allocatedBytes > 0) {
			adjustTextureMemory(-this.allocatedBytes, -1)
			this.allocatedBytes = 0
		}
	}

	public async loadUrl(url: string, side: number): Promise<void> {
		this.available = false
		this.width = 0
		this.height = 0

		const image = new Image()
		image.crossOrigin = 'cors'	// or 'anonymous'
		image.src = url
		await image.decode()
		this.uploadCanvas(image, image.width, image.height, side)
	}

	public async setFromArrayBuffer(data: Uint8Array, side: number, type?: string): Promise<void> {
		this.available = false
		this.width = 0
		this.height = 0

		const image = new Image()
		const blob = (type !== undefined) ? new Blob([data as BlobPart], { type: type }) : new Blob([data as BlobPart])
		const url = URL.createObjectURL(blob)
		image.src = url
		await image.decode()
		URL.revokeObjectURL(url)
		this.uploadCanvas(image, image.width, image.height, side)
	}

	public uploadArrayBuffer(buffer: Uint8Array | Uint16Array, width: number, height: number, side: number): void {
		const gl = this.context.gl
		this.width = width
		this.height = height
		this.textureId ??= gl.createTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_CUBE_MAP, this.textureId)
		gl.texImage2D(WebGL2RenderingContext.TEXTURE_CUBE_MAP_POSITIVE_X + side, 0, this.internalFormat, this.width, this.height, 0,
			this.imageFormat, this.dataType, buffer)
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_CUBE_MAP, null)
		this.available = true
		this.accountAllocation(false)
	}

	public uploadCanvas(source: TexImageSource, width: number, height: number, side: number): void {
		const gl = this.context.gl
		this.width = width
		this.height = height
		this.textureId ??= gl.createTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_CUBE_MAP, this.textureId)
		gl.texImage2D(WebGL2RenderingContext.TEXTURE_CUBE_MAP_POSITIVE_X + side, 0, this.internalFormat, this.imageFormat, this.dataType, source)
		gl.generateMipmap(WebGL2RenderingContext.TEXTURE_CUBE_MAP)
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_CUBE_MAP, null)
		this.available = true
		this.accountAllocation(true)
	}

	public setRepeating(repeat: boolean): void {
		this.wrapS = repeat ? WebGL2RenderingContext.REPEAT : WebGL2RenderingContext.CLAMP_TO_EDGE
		this.wrapT = repeat ? WebGL2RenderingContext.REPEAT : WebGL2RenderingContext.CLAMP_TO_EDGE
	}
}